import {
  DatePicker,
  Button,
  IconButton,
  SwitchField,
  Callout,
  Skeleton,
} from '@dev-spendesk/grapes';
import { addMinutes, subMinutes, isSameDay } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { usePayableCounts } from 'modules/bookkeep/graphql/hooks';
import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { routeFor, routes } from 'src/core/constants/routes';
import { useBookkeepingSettings } from 'src/core/modules/bookkeep/apis/useBookkeepingSettings';
import { useSetBookkeepingSettings } from 'src/core/modules/bookkeep/apis/useSetBookkeepingSettings';

import { BookkeepingStartDateConfirmationModal } from './BookkeepingStartDateConfirmationModal';

export function BookkeepingStartDateSection() {
  const { t } = useTranslation('global');
  const history = useHistory();
  const companyId = useCompanyId();

  const bookkeepingSettings = useBookkeepingSettings();
  const bookkeepingSettingsValue = unwrapQuery(bookkeepingSettings);
  const bookkeepingStartDate = bookkeepingSettingsValue?.bookkeepingStartDate;

  const isComingFromPreparePage =
    history.location.state?.isNewBookkeepingPrepareAfresh;

  const redirectTo = isComingFromPreparePage
    ? routeFor(routes.EXPENSE_INBOX_PREPARE.path, {
        company: companyId,
      })
    : undefined;

  const [date, setDate] = useState<Date | null | undefined>(undefined);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [setBookkeepingSettings] = useSetBookkeepingSettings();
  const [isEnabled, setIsEnabled] = useState(!!date);

  useEffect(() => {
    setDate(
      typeof bookkeepingStartDate === 'string'
        ? new Date(bookkeepingStartDate)
        : undefined,
    );
  }, [bookkeepingStartDate]);

  useMemo(() => setIsEnabled(!!date), [date]);

  const onConfirm = async () => {
    const newDate = localeDateToUTC(date);
    await setBookkeepingSettings({
      bookkeepingStartDate: newDate,
      editPayablesAfterExport: {
        lockAfter: bookkeepingSettingsValue?.editPayablesAfterExport.lockAfter,
        lockAfterUnit:
          bookkeepingSettingsValue?.editPayablesAfterExport.lockAfterUnit,
        hasOptedIn:
          !!bookkeepingSettingsValue?.editPayablesAfterExport.hasOptedIn,
      },
    });
    setDate(newDate);
    setIsModalOpen(false);
    if (redirectTo) {
      history.push(redirectTo);
    }
  };

  const payableCountsQueryResult = usePayableCounts({ to: date || undefined });

  if (bookkeepingSettings.status === 'loading') {
    return (
      <div className="box">
        <Skeleton width="75%" height="20px" className="my-4" />
      </div>
    );
  }

  return (
    <>
      <SwitchField
        label={
          <div className="heading-l">
            {t('accounting.bookkeepingStartDate.title')}
          </div>
        }
        fit="parent"
        isChecked={isEnabled}
        onChange={async () => {
          setDate(date !== undefined ? undefined : null);
          setIsEnabled(!isEnabled);
          if (bookkeepingStartDate && date !== undefined) {
            await setBookkeepingSettings({
              bookkeepingStartDate: null,
              editPayablesAfterExport: { hasOptedIn: false },
            });
          }
        }}
      />

      {bookkeepingSettings.status === 'error' && (
        <Callout
          title={t('accounting.bookkeepingStartDate.error')}
          variant="alert"
        />
      )}

      {date !== undefined && (
        <div className="flex flex-col gap-24">
          <p className="text-content-primary leading-5">
            {t('accounting.bookkeepingStartDate.content')}
          </p>
          <div className="flex flex-row gap-8">
            <DatePicker
              placement="bottom-start"
              value={date || undefined}
              onChange={(newDate) => {
                setDate(newDate || undefined);
              }}
              placeholder={t('accounting.bookkeepingStartDate.placeholder')}
              className="w-[500px]"
            />
            {date && (
              <IconButton
                iconName="cross"
                variant="tertiaryNeutral"
                aria-label={t('misc.delete')}
                onClick={() => {
                  setDate(null);
                }}
              />
            )}
          </div>
          <Button
            isDisabled={isSameDay(
              bookkeepingStartDate ?? new Date(),
              date ?? new Date(),
            )}
            text={t('accounting.bookkeepingStartDate.save')}
            variant="primaryBrand"
            className="w-[fit-content]"
            onClick={async () => {
              if (!date) {
                await onConfirm();
                return;
              }
              setIsModalOpen(true);
            }}
          />
        </div>
      )}

      <BookkeepingStartDateConfirmationModal
        bookkeepingStartDate={date}
        hiddenPayablesCount={payableCountsQueryResult.data?.toPrepareCount}
        isOpen={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
        }}
        onConfirm={onConfirm}
      />
    </>
  );
}

// Will transform locale "2021-03-30T22:00:00.000Z"
// into UTC "2021-03-31T00:00:00.000Z"
// We need this transformation because we receive the locale date
// from the user input, but we need to send it to the server
// in a standardize way (UTC)
function localeDateToUTC(
  date: Date | null | undefined,
): Date | null | undefined {
  if (!date) {
    return date;
  }
  const offset = date.getTimezoneOffset();
  return Math.sign(offset) !== -1
    ? subMinutes(date, Math.abs(offset))
    : addMinutes(date, Math.abs(offset));
}
